import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Modal } from '../ui/Modal';
import { PrimaryButton, SecondaryButton } from '../../ui/common';
import { useTranslation } from '../../translations';
import { useToast } from '../ui/Toast';
import { API_BASE } from '../../config';
import {
  EXPORT_GROUPS, EXPORT_FORMATS, orderedFields, groupState, toggleGroup, toggleField,
  exportQuery, previewRows, columnLetter, filenameFromDisposition, loadExportPrefs, saveExportPrefs,
} from '../../utils/clientExport';

// Eksport kartoteki klientów (26.10.2026). Okno ma trzy decyzje (którzy klienci, jakie cechy,
// jaki format) i jeden dowód: podgląd pliku narysowany jak arkusz, z literami kolumn i numerami
// wierszy, w tej samej kolejności kolumn i z tymi samymi nagłówkami co plik z serwera
// (`utils/clientExport.js` jest lustrem `Login/backend/lib/clientExport.js`).

const PREVIEW_ROWS = 3;

const pluralForm = (n) => new Intl.PluralRules('pl-PL').select(n);

export default function ClientExportModal({
  open, onClose, listClients, allClients, listActive, listParams, allCountCapped,
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [prefs] = useState(() => loadExportPrefs());
  const [fields, setFields] = useState(prefs.fields);
  const [format, setFormat] = useState(prefs.format);
  // 'list' = to, co widać na liście (z wyszukiwaniem, filtrem i sortowaniem); bez zawężenia to
  // wszyscy klienci w kolejności z ekranu, więc wybór zakresu pokazuje się dopiero przy zawężeniu.
  const [scope, setScope] = useState('list');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Każde otwarcie zaczyna od listy: tego zwykle szuka ktoś, kto najpierw filtruje, a potem eksportuje.
  useEffect(() => {
    if (open) { setScope('list'); setError(''); }
  }, [open]);

  useEffect(() => { saveExportPrefs({ fields, format }); }, [fields, format]);

  const rows = scope === 'list' ? listClients : allClients;
  const columns = useMemo(() => orderedFields(fields), [fields]);
  const preview = useMemo(() => previewRows(rows, fields, PREVIEW_ROWS), [rows, fields]);

  const label = (key) => t(`clientsExport.field.${key}`);
  const groupLabel = (id) => t(`clientsExport.group.${id}`);

  const clientsCount = (n, capped) => {
    const liczba = capped ? `${n}+` : String(n);
    switch (pluralForm(n)) {
      case 'one': return t('clientsExport.clientsOne', { n: liczba });
      case 'few': return t('clientsExport.clientsFew', { n: liczba });
      default: return t('clientsExport.clientsMany', { n: liczba });
    }
  };
  const columnsCount = (n) => {
    switch (pluralForm(n)) {
      case 'one': return t('clientsExport.columnsOne', { n });
      case 'few': return t('clientsExport.columnsFew', { n });
      default: return t('clientsExport.columnsMany', { n });
    }
  };

  const listDescription = () => {
    const czesci = [];
    if (listParams.q && listParams.q.trim()) czesci.push(t('clientsExport.scopeSearch', { q: listParams.q.trim() }));
    if (listParams.filter === 'recent') czesci.push(t('clientsExport.scopeRecent'));
    if (listParams.filter === 'frequent') czesci.push(t('clientsExport.scopeFrequent'));
    return czesci.join(' · ');
  };

  const download = async () => {
    if (!columns.length || busy) return;
    setBusy(true);
    setError('');
    try {
      const qs = exportQuery({ format, fields, scope, ...listParams });
      const res = await fetch(`${API_BASE}/api/clients/export?${qs}`, { credentials: 'include' });
      if (!res.ok) {
        let msg = '';
        try { msg = (await res.json()).error || ''; } catch (_) { /* odpowiedź bez JSON */ }
        if (res.status === 403) msg = t('clientsExport.errorAdmin');
        else if (res.status === 401) msg = t('clientsExport.errorSession');
        throw new Error(msg || t('clientsExport.errorGeneric'));
      }
      const blob = await res.blob();
      const name = filenameFromDisposition(res.headers.get('Content-Disposition'), `klienci.${format}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(t('clientsExport.downloaded', { name }));
      onClose();
    } catch (e) {
      setError(e.message || t('clientsExport.errorGeneric'));
    } finally {
      setBusy(false);
    }
  };

  const ext = format === 'csv' ? '.csv' : '.xlsx';

  // Podgląd stoi w nieprzewijanym pasku pod tytułem: plik rośnie na oczach przy każdym
  // zaznaczeniu, a nie dopiero po przewinięciu okna do końca.
  const podglad = (
    <PreviewPane>
      <SectionTitle as="h3">{t('clientsExport.previewTitle')}</SectionTitle>
      {columns.length === 0 ? (
        <EmptyPreview data-cy="clients-export-preview-empty">{t('clientsExport.previewEmpty')}</EmptyPreview>
      ) : (
        <>
          <Sheet data-cy="clients-export-preview" tabIndex={0} aria-label={t('clientsExport.previewTitle')}>
            <table>
              <thead>
                <tr>
                  <Corner aria-hidden="true" />
                  {columns.map((c, i) => <Letter key={c.key} scope="col" aria-hidden="true">{columnLetter(i)}</Letter>)}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <RowNo aria-hidden="true">1</RowNo>
                  {columns.map((c) => <HeaderCell key={c.key}>{c.header}</HeaderCell>)}
                </tr>
                {preview.map((r, ri) => (
                  <tr key={ri}>
                    <RowNo aria-hidden="true">{ri + 2}</RowNo>
                    {r.map((v, ci) => (
                      <Cell key={columns[ci].key} $empty={!v}>{v || '—'}</Cell>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Sheet>
          <PreviewNote>
            {rows.length > PREVIEW_ROWS
              ? t('clientsExport.previewMore', { shown: Math.min(PREVIEW_ROWS, rows.length), total: rows.length })
              : t('clientsExport.previewAll')}
          </PreviewNote>
        </>
      )}
    </PreviewPane>
  );

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={t('clientsExport.title')}
      subtitle={t('clientsExport.subtitle')}
      size="lg"
      fullScreenOnMobile
      data-cy="clients-export-modal"
      toolbar={podglad}
      footer={(
        <Footer>
          <Summary data-cy="clients-export-summary">
            {clientsCount(rows.length, (scope === 'all' || !listActive) && allCountCapped)}
            <Dot aria-hidden="true">·</Dot>
            {columnsCount(columns.length)}
          </Summary>
          <FooterButtons>
            <SecondaryButton type="button" onClick={onClose} disabled={busy}>{t('clientsExport.cancel')}</SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={download}
              disabled={!columns.length || busy}
              data-cy="clients-export-download"
            >
              {busy ? t('clientsExport.preparing') : t('clientsExport.download', { ext })}
            </PrimaryButton>
          </FooterButtons>
        </Footer>
      )}
    >
      <Layout>
        {error && <ErrorBox role="alert" data-cy="clients-export-error">{error}</ErrorBox>}
        <Controls>
          {listActive && (
            <Section>
              <SectionTitle id="clients-export-scope">{t('clientsExport.scopeTitle')}</SectionTitle>
              <ScopeGrid role="radiogroup" aria-labelledby="clients-export-scope">
                <ScopeCard
                  type="button"
                  role="radio"
                  aria-checked={scope === 'list'}
                  $active={scope === 'list'}
                  onClick={() => setScope('list')}
                  data-cy="clients-export-scope-list"
                >
                  <ScopeName>{t('clientsExport.scopeList')}</ScopeName>
                  <ScopeCount>{clientsCount(listClients.length)}</ScopeCount>
                  {listDescription() && <ScopeHint>{listDescription()}</ScopeHint>}
                </ScopeCard>
                <ScopeCard
                  type="button"
                  role="radio"
                  aria-checked={scope === 'all'}
                  $active={scope === 'all'}
                  onClick={() => setScope('all')}
                  data-cy="clients-export-scope-all"
                >
                  <ScopeName>{t('clientsExport.scopeAll')}</ScopeName>
                  <ScopeCount>{clientsCount(allClients.length, allCountCapped)}</ScopeCount>
                  <ScopeHint>{t('clientsExport.scopeAllHint')}</ScopeHint>
                </ScopeCard>
              </ScopeGrid>
            </Section>
          )}

          <Section>
            <SectionHead>
              <SectionTitle as="h3">{t('clientsExport.fieldsTitle')}</SectionTitle>
              {columns.length > 0 && (
                <ClearLink type="button" onClick={() => setFields([])} data-cy="clients-export-clear">
                  {t('clientsExport.clearFields')}
                </ClearLink>
              )}
            </SectionHead>
            <Groups>
              {EXPORT_GROUPS.map((g) => {
                const stan = groupState(fields, g);
                const wybrane = g.fields.filter((k) => fields.includes(k)).length;
                return (
                  <Group key={g.id} data-cy={`clients-export-group-${g.id}`}>
                    <GroupHead>
                      <GroupToggle>
                        <input
                          type="checkbox"
                          checked={stan === 'all'}
                          ref={(el) => { if (el) el.indeterminate = stan === 'some'; }}
                          onChange={() => setFields((f) => toggleGroup(f, g))}
                          data-cy={`clients-export-group-toggle-${g.id}`}
                        />
                        <span>{groupLabel(g.id)}</span>
                      </GroupToggle>
                      <GroupCount aria-hidden="true">{wybrane}/{g.fields.length}</GroupCount>
                    </GroupHead>
                    <Chips>
                      {g.fields.map((k) => {
                        const on = fields.includes(k);
                        return (
                          <Chip
                            key={k}
                            type="button"
                            aria-pressed={on}
                            $on={on}
                            onClick={() => setFields((f) => toggleField(f, k))}
                            data-cy={`clients-export-field-${k}`}
                          >
                            <Tick aria-hidden="true" $on={on} />
                            {label(k)}
                          </Chip>
                        );
                      })}
                    </Chips>
                  </Group>
                );
              })}
            </Groups>
          </Section>

          <Section>
            <SectionTitle as="h3" id="clients-export-format">{t('clientsExport.formatTitle')}</SectionTitle>
            <Segmented role="radiogroup" aria-labelledby="clients-export-format">
              {EXPORT_FORMATS.map((f) => (
                <Segment
                  key={f}
                  type="button"
                  role="radio"
                  aria-checked={format === f}
                  $active={format === f}
                  onClick={() => setFormat(f)}
                  data-cy={`clients-export-format-${f}`}
                >
                  {t(`clientsExport.format.${f}`)}
                </Segment>
              ))}
            </Segmented>
            <FormatHint>{t(`clientsExport.formatHint.${format}`)}</FormatHint>
          </Section>
        </Controls>

      </Layout>
    </Modal>
  );
}


/* ── Wygląd ─────────────────────────────────────────────────────────────────────────────── */

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 22px;
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--text-muted);
`;

const ClearLink = styled.button`
  border: 0;
  background: none;
  padding: 2px 0;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-muted);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  &:hover { color: var(--text); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 4px; }
`;

const ScopeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  @media (max-width: 560px) { grid-template-columns: minmax(0, 1fr); }
`;

const ScopeCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 12px 14px;
  text-align: left;
  border-radius: var(--r-md);
  border: 1px solid ${(p) => (p.$active ? 'var(--primary)' : 'var(--input-border)')};
  box-shadow: ${(p) => (p.$active ? 'inset 0 0 0 1px var(--primary)' : 'none')};
  background: ${(p) => (p.$active ? 'var(--info-bg)' : 'var(--card-bg)')};
  color: var(--text);
  font: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover { border-color: var(--primary); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

const ScopeName = styled.span`
  font-weight: 700;
  font-size: 0.9375rem;
`;

const ScopeCount = styled.span`
  font-size: 0.8125rem;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

const ScopeHint = styled.span`
  margin-top: 4px;
  font-size: 0.75rem;
  color: var(--text-muted);
  overflow-wrap: anywhere;
`;

const Groups = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  align-items: start;
  @media (max-width: 640px) { grid-template-columns: minmax(0, 1fr); }
`;

const Group = styled.div`
  padding: 10px 12px 12px;
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  background: var(--card-bg);
`;

const GroupHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const GroupToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  input { width: 16px; height: 16px; margin: 0; accent-color: var(--primary); cursor: pointer; }
`;

const GroupCount = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px 5px 8px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$on ? 'var(--primary)' : 'var(--input-border)')};
  background: ${(p) => (p.$on ? 'var(--info-bg)' : 'transparent')};
  color: ${(p) => (p.$on ? 'var(--primary)' : 'var(--text)')};
  font: inherit;
  font-size: 0.8125rem;
  font-weight: ${(p) => (p.$on ? 600 : 500)};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover { border-color: var(--primary); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

// Znacznik zamiast samej zmiany koloru: wybrana cecha ma być widoczna także bez koloru.
const Tick = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  border: 1.5px solid currentColor;
  position: relative;
  flex: none;
  opacity: ${(p) => (p.$on ? 1 : 0.45)};
  &::after {
    content: '';
    display: ${(p) => (p.$on ? 'block' : 'none')};
    position: absolute;
    left: 3px;
    top: 0;
    width: 3px;
    height: 7px;
    border: solid currentColor;
    border-width: 0 1.5px 1.5px 0;
    transform: rotate(45deg);
  }
`;

const Segmented = styled.div`
  display: inline-flex;
  align-self: flex-start;
  padding: 3px;
  border-radius: var(--r-md);
  border: 1px solid var(--input-border);
  background: var(--input-bg);
`;

const Segment = styled.button`
  padding: 7px 14px;
  border: 0;
  border-radius: calc(var(--r-md) - 3px);
  background: ${(p) => (p.$active ? 'var(--card-bg)' : 'transparent')};
  box-shadow: ${(p) => (p.$active ? 'var(--shadow-sm, 0 1px 2px rgba(0,0,0,0.08))' : 'none')};
  color: ${(p) => (p.$active ? 'var(--text)' : 'var(--text-muted)')};
  font: inherit;
  font-size: 0.875rem;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  cursor: pointer;
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 1px; }
`;

const FormatHint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--text-muted);
  line-height: 1.45;
`;

const PreviewPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

// Podgląd jako arkusz: litery kolumn i numery wierszy są prawdą o pliku (tak go zobaczy Excel),
// a nie ozdobą. Kolor obramowania i tła tylko ze zmiennych motywu.
const Sheet = styled.div`
  overflow-x: auto;
  border: 1px solid var(--input-border);
  border-radius: var(--r-sm);
  background: var(--card-bg);
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

  table {
    border-collapse: collapse;
    font-size: 0.8125rem;
    font-variant-numeric: tabular-nums;
    min-width: 100%;
  }
  th, td {
    border-right: 1px solid var(--input-border);
    border-bottom: 1px solid var(--input-border);
    padding: 6px 10px;
    white-space: nowrap;
    text-align: left;
  }
  tr:last-child td { border-bottom: 0; }
  th:last-child, td:last-child { border-right: 0; }
`;

const Corner = styled.th`
  width: 34px;
  background: var(--hover-bg);
`;

const Letter = styled.th`
  background: var(--hover-bg);
  color: var(--text-muted);
  font-weight: 600;
  font-size: 0.6875rem;
  text-align: center !important;
  padding: 3px 10px !important;
`;

const RowNo = styled.td`
  width: 34px;
  background: var(--hover-bg);
  color: var(--text-muted);
  font-size: 0.6875rem;
  text-align: center !important;
`;

const HeaderCell = styled.td`
  font-weight: 700;
  color: var(--text);
`;

const Cell = styled.td`
  color: ${(p) => (p.$empty ? 'var(--text-muted)' : 'var(--text)')};
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PreviewNote = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

// Ta sama wysokość co arkusz z trzema wierszami: pasek nie skacze, gdy znikną wszystkie cechy.
const EmptyPreview = styled.div`
  min-height: 142px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  border: 1px dashed var(--input-border);
  border-radius: var(--r-sm);
  font-size: 0.875rem;
  color: var(--text-muted);
  text-align: center;
`;

const ErrorBox = styled.div`
  padding: 10px 12px;
  border-radius: var(--r-sm);
  background: color-mix(in srgb, var(--danger) 10%, transparent);
  color: var(--danger);
  font-size: 0.875rem;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;
`;

const Summary = styled.div`
  font-size: 0.875rem;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

const Dot = styled.span`
  margin: 0 6px;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-left: auto;
`;
